(function () {
  'use strict';

  const SELECTORS = {
    header: '[data-rnv2-header]',
    menuButton: '[data-rnv2-menu-button]',
    mobileMenu: '[data-rnv2-mobile-menu]',
    searchOpen: '[data-rnv2-search-open]',
    searchClose: '[data-rnv2-search-close]',
    searchPanel: '[data-rnv2-search-panel]',
    reveal: '.rnv2-reveal',
    faqItem: '[data-rnv2-faq-item]',
    faqButton: '[data-rnv2-faq-button]',
    productSection: '[data-rnv2-product-section]',
    productForm: '[data-rnv2-product-form]',
    quantityMinus: '[data-rnv2-quantity-minus]',
    quantityPlus: '[data-rnv2-quantity-plus]',
    quantityInput: '[data-rnv2-quantity-input]',
    variantSelect: '[data-rnv2-variant-select]',
    addButton: '[data-rnv2-add-button]',
    addText: '[data-rnv2-add-text]',
    productMessage: '[data-rnv2-product-message]',
    cartCount: '[data-rnv2-cart-count]'
  };

  const CLASS_NAMES = {
    active: 'rnv2-active',
    open: 'rnv2-open',
    sticky: 'rnv2-sticky',
    visible: 'rnv2-visible',
    noScroll: 'rnv2-no-scroll'
  };

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  function lockPage() {
    document.documentElement.classList.add(CLASS_NAMES.noScroll);
    document.body.classList.add(CLASS_NAMES.noScroll);
  }

  function unlockPage() {
    document.documentElement.classList.remove(CLASS_NAMES.noScroll);
    document.body.classList.remove(CLASS_NAMES.noScroll);
  }

  function initHeader(root) {
    const header = root.querySelector(SELECTORS.header);

    if (!header || header.dataset.rnv2Initialized === 'true') {
      return;
    }

    header.dataset.rnv2Initialized = 'true';

    const menuButton = header.querySelector(SELECTORS.menuButton);
    const mobileMenu = header.querySelector(SELECTORS.mobileMenu);
    const searchOpenButton = header.querySelector(SELECTORS.searchOpen);
    const searchPanel = document.querySelector(SELECTORS.searchPanel);
    const searchCloseButtons = searchPanel
      ? searchPanel.querySelectorAll(SELECTORS.searchClose)
      : [];

    let headerOffset = header.offsetTop + 34;

    function updateHeaderOffset() {
      headerOffset = header.offsetTop + 34;
    }

    function updateStickyHeader() {
      if (window.scrollY > headerOffset) {
        header.classList.add(CLASS_NAMES.sticky);
      } else {
        header.classList.remove(CLASS_NAMES.sticky);
      }
    }

    function closeMobileMenu() {
      if (!menuButton || !mobileMenu) {
        return;
      }

      menuButton.classList.remove(CLASS_NAMES.active);
      mobileMenu.classList.remove(CLASS_NAMES.open);
      menuButton.setAttribute('aria-expanded', 'false');
    }

    function openMobileMenu() {
      if (!menuButton || !mobileMenu) {
        return;
      }

      menuButton.classList.add(CLASS_NAMES.active);
      mobileMenu.classList.add(CLASS_NAMES.open);
      menuButton.setAttribute('aria-expanded', 'true');
    }

    function toggleMobileMenu() {
      if (!menuButton || !mobileMenu) {
        return;
      }

      const isOpen = mobileMenu.classList.contains(CLASS_NAMES.open);

      if (isOpen) {
        closeMobileMenu();
      } else {
        openMobileMenu();
      }
    }

    function openSearch() {
      if (!searchPanel) {
        return;
      }

      closeMobileMenu();
      searchPanel.hidden = false;
      lockPage();

      window.requestAnimationFrame(function () {
        const input = searchPanel.querySelector('input[type="search"]');

        if (input) {
          input.focus();
        }
      });
    }

    function closeSearch() {
      if (!searchPanel) {
        return;
      }

      searchPanel.hidden = true;
      unlockPage();
    }

    if (menuButton) {
      menuButton.addEventListener('click', toggleMobileMenu);
    }

    if (mobileMenu) {
      mobileMenu.querySelectorAll('a').forEach(function (link) {
        link.addEventListener('click', closeMobileMenu);
      });
    }

    if (searchOpenButton) {
      searchOpenButton.addEventListener('click', openSearch);
    }

    searchCloseButtons.forEach(function (button) {
      button.addEventListener('click', closeSearch);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeSearch();
        closeMobileMenu();
      }
    });

    window.addEventListener(
      'scroll',
      function () {
        window.requestAnimationFrame(updateStickyHeader);
      },
      { passive: true }
    );

    window.addEventListener(
      'resize',
      function () {
        updateHeaderOffset();

        if (window.innerWidth > 1100) {
          closeMobileMenu();
        }
      },
      { passive: true }
    );

    updateStickyHeader();
  }

  function initRevealAnimations(root) {
    const elements = root.querySelectorAll(SELECTORS.reveal);

    if (!elements.length) {
      return;
    }

    if (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      elements.forEach(function (element) {
        element.classList.add(CLASS_NAMES.visible);
      });

      return;
    }

    if (!('IntersectionObserver' in window)) {
      elements.forEach(function (element) {
        element.classList.add(CLASS_NAMES.visible);
      });

      return;
    }

    const observer = new IntersectionObserver(
      function (entries, currentObserver) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add(CLASS_NAMES.visible);
          currentObserver.unobserve(entry.target);
        });
      },
      {
        root: null,
        threshold: 0.12,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    elements.forEach(function (element, index) {
      if (element.dataset.rnv2RevealInitialized === 'true') {
        return;
      }

      element.dataset.rnv2RevealInitialized = 'true';
      element.style.transitionDelay = Math.min(index % 4, 3) * 80 + 'ms';
      observer.observe(element);
    });
  }

  function initSmoothAnchors(root) {
    const links = root.querySelectorAll('a[href^="#"]');

    links.forEach(function (link) {
      if (link.dataset.rnv2AnchorInitialized === 'true') {
        return;
      }

      link.dataset.rnv2AnchorInitialized = 'true';

      link.addEventListener('click', function (event) {
        const href = link.getAttribute('href');

        if (!href || href === '#') {
          return;
        }

        const target = document.querySelector(href);

        if (!target) {
          return;
        }

        event.preventDefault();

        const headerHeight =
          document.querySelector('.rnv2-header__main')?.offsetHeight || 0;

        const targetPosition =
          target.getBoundingClientRect().top +
          window.pageYOffset -
          headerHeight;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      });
    });
  }

  function initFaq(root) {
    const faqItems = root.querySelectorAll(SELECTORS.faqItem);

    faqItems.forEach(function (item) {
      if (item.dataset.rnv2Initialized === 'true') {
        return;
      }

      item.dataset.rnv2Initialized = 'true';

      const button = item.querySelector(SELECTORS.faqButton);

      if (!button) {
        return;
      }

      button.addEventListener('click', function () {
        const isOpen = item.classList.contains(CLASS_NAMES.open);
        const parent = item.parentElement;

        if (parent) {
          parent
            .querySelectorAll(SELECTORS.faqItem)
            .forEach(function (otherItem) {
              if (otherItem === item) {
                return;
              }

              otherItem.classList.remove(CLASS_NAMES.open);

              const otherButton = otherItem.querySelector(
                SELECTORS.faqButton
              );

              if (otherButton) {
                otherButton.setAttribute('aria-expanded', 'false');
              }
            });
        }

        item.classList.toggle(CLASS_NAMES.open, !isOpen);
        button.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  function initQuantityControls(productSection) {
    const minusButton = productSection.querySelector(
      SELECTORS.quantityMinus
    );

    const plusButton = productSection.querySelector(
      SELECTORS.quantityPlus
    );

    const input = productSection.querySelector(SELECTORS.quantityInput);

    if (!input) {
      return;
    }

    function normalizeQuantity(value) {
      const numericValue = Number.parseInt(value, 10);

      if (Number.isNaN(numericValue) || numericValue < 1) {
        return 1;
      }

      return numericValue;
    }

    function updateQuantity(change) {
      const currentValue = normalizeQuantity(input.value);
      input.value = Math.max(1, currentValue + change);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    input.addEventListener('change', function () {
      input.value = normalizeQuantity(input.value);
    });

    if (minusButton) {
      minusButton.addEventListener('click', function () {
        updateQuantity(-1);
      });
    }

    if (plusButton) {
      plusButton.addEventListener('click', function () {
        updateQuantity(1);
      });
    }
  }

  function initVariantSelector(productSection) {
    const select = productSection.querySelector(SELECTORS.variantSelect);
    const price = productSection.querySelector('[data-rnv2-price]');
    const comparePrice = productSection.querySelector(
      '[data-rnv2-compare-price]'
    );
    const addButton = productSection.querySelector(SELECTORS.addButton);
    const addText = productSection.querySelector(SELECTORS.addText);

    if (!select) {
      return;
    }

    function updateVariantDisplay() {
      const option = select.options[select.selectedIndex];

      if (!option) {
        return;
      }

      const available = option.dataset.available === 'true';
      const optionPrice = option.dataset.price || '';
      const optionComparePrice = option.dataset.comparePrice || '';

      if (price && optionPrice) {
        price.textContent = optionPrice;
      }

      if (comparePrice) {
        if (
          optionComparePrice &&
          optionComparePrice !== optionPrice &&
          optionComparePrice !== ''
        ) {
          comparePrice.textContent = optionComparePrice;
          comparePrice.hidden = false;
        } else {
          comparePrice.hidden = true;
        }
      }

      if (addButton) {
        addButton.disabled = !available;
      }

      if (addText) {
        addText.textContent = available
          ? 'Kosárba teszem'
          : 'Jelenleg nem elérhető';
      }
    }

    select.addEventListener('change', updateVariantDisplay);
    updateVariantDisplay();
  }

  async function fetchCart() {
    const response = await fetch(
      window.Shopify?.routes?.root
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js',
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('A kosár nem frissíthető.');
    }

    return response.json();
  }

  function updateCartCounters(count) {
    document.querySelectorAll(SELECTORS.cartCount).forEach(function (counter) {
      counter.textContent = count;
    });
  }

  function initAjaxProductForm(productSection) {
    const form = productSection.querySelector(SELECTORS.productForm);

    if (!form) {
      return;
    }

    const addButton = form.querySelector(SELECTORS.addButton);
    const addText = form.querySelector(SELECTORS.addText);
    const message = form.querySelector(SELECTORS.productMessage);

    form.addEventListener('submit', async function (event) {
      event.preventDefault();

      if (!addButton || addButton.disabled) {
        return;
      }

      const originalText = addText
        ? addText.textContent
        : 'Kosárba teszem';

      addButton.disabled = true;

      if (addText) {
        addText.textContent = 'Hozzáadás...';
      }

      if (message) {
        message.textContent = '';
        message.style.color = '';
      }

      try {
        const formData = new FormData(form);

        const addResponse = await fetch(
          window.Shopify?.routes?.root
            ? window.Shopify.routes.root + 'cart/add.js'
            : '/cart/add.js',
          {
            method: 'POST',
            headers: {
              Accept: 'application/json'
            },
            body: formData
          }
        );

        const addResult = await addResponse.json();

        if (!addResponse.ok) {
          throw new Error(
            addResult.description ||
              addResult.message ||
              'A termék nem adható a kosárhoz.'
          );
        }

        const cart = await fetchCart();
        updateCartCounters(cart.item_count);

        if (addText) {
          addText.textContent = 'Kosárba helyezve ✓';
        }

        if (message) {
          message.textContent = 'A termék sikeresen a kosárba került.';
          message.style.color = '#178b59';
        }

        document.dispatchEvent(
          new CustomEvent('rnv2:cart-updated', {
            detail: {
              cart: cart,
              item: addResult
            }
          })
        );

        window.setTimeout(function () {
          if (addText) {
            addText.textContent = originalText;
          }

          addButton.disabled = false;
        }, 1800);
      } catch (error) {
        if (addText) {
          addText.textContent = originalText;
        }

        if (message) {
          message.textContent =
            error.message ||
            'Hiba történt. Kérjük, próbáld újra.';
          message.style.color = '#c62828';
        }

        addButton.disabled = false;
      }
    });
  }

  function initProductSections(root) {
    const productSections = root.querySelectorAll(
      SELECTORS.productSection
    );

    productSections.forEach(function (productSection) {
      if (productSection.dataset.rnv2Initialized === 'true') {
        return;
      }

      productSection.dataset.rnv2Initialized = 'true';

      initQuantityControls(productSection);
      initVariantSelector(productSection);
      initAjaxProductForm(productSection);
    });
  }

  function initParallax(root) {
    const hero = root.querySelector('.rnv2-hero');
    const heroImage = hero
      ? hero.querySelector('.rnv2-hero__image')
      : null;

    if (!hero || !heroImage) {
      return;
    }

    if (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    if (window.innerWidth <= 760) {
      return;
    }

    let ticking = false;

    function updateParallax() {
      const heroRect = hero.getBoundingClientRect();

      if (heroRect.bottom > 0 && heroRect.top < window.innerHeight) {
        const offset = Math.max(0, -heroRect.top) * 0.12;
        heroImage.style.transform = 'scale(1.04) translateY(' + offset + 'px)';
      }

      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(updateParallax);
          ticking = true;
        }
      },
      { passive: true }
    );

    updateParallax();
  }

  function initPointerGlow(root) {
    const cards = root.querySelectorAll(
      '.rnv2-feature-card, .rnv2-spec-card, .rnv2-radar-capability'
    );

    cards.forEach(function (card) {
      if (card.dataset.rnv2GlowInitialized === 'true') {
        return;
      }

      card.dataset.rnv2GlowInitialized = 'true';

      card.addEventListener('pointermove', function (event) {
        const rect = card.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        card.style.setProperty('--rnv2-pointer-x', x + 'px');
        card.style.setProperty('--rnv2-pointer-y', y + 'px');
      });
    });
  }

  function addDynamicStyles() {
    if (document.getElementById('Rnv2DynamicStyles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'Rnv2DynamicStyles';

    style.textContent = `
      .rnv2-feature-card::before,
      .rnv2-spec-card::before,
      .rnv2-radar-capability::before {
        position: absolute;
        z-index: 0;
        width: 260px;
        height: 260px;
        border-radius: 50%;
        background: radial-gradient(
          circle,
          rgba(22, 140, 255, 0.11),
          transparent 68%
        );
        content: "";
        pointer-events: none;
        opacity: 0;
        transform: translate(
          calc(var(--rnv2-pointer-x, 50%) - 130px),
          calc(var(--rnv2-pointer-y, 50%) - 130px)
        );
        transition: opacity 0.3s ease;
      }

      .rnv2-feature-card:hover::before,
      .rnv2-spec-card:hover::before,
      .rnv2-radar-capability:hover::before {
        opacity: 1;
      }

      .rnv2-feature-card > *,
      .rnv2-spec-card > *,
      .rnv2-radar-capability > * {
        position: relative;
        z-index: 1;
      }

      .rnv2-feature-card .rnv2-feature-card__image,
      .rnv2-feature-card .rnv2-feature-map,
      .rnv2-feature-card .rnv2-satellite-visual,
      .rnv2-feature-card .rnv2-climb-chart,
      .rnv2-feature-card .rnv2-connection-cloud {
        position: absolute;
      }

      .rnv2-feature-card,
      .rnv2-spec-card,
      .rnv2-radar-capability {
        transition:
          transform 0.35s ease,
          box-shadow 0.35s ease;
      }

      .rnv2-feature-card:hover,
      .rnv2-spec-card:hover,
      .rnv2-radar-capability:hover {
        transform: translateY(-5px);
        box-shadow: 0 25px 60px rgba(0, 0, 0, 0.13);
      }

      .rnv2-radar-capability:hover {
        box-shadow: 0 25px 60px rgba(0, 0, 0, 0.38);
      }

      @media screen and (max-width: 760px) {
        .rnv2-feature-card:hover,
        .rnv2-spec-card:hover,
        .rnv2-radar-capability:hover {
          transform: none;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function initialize(root) {
    const currentRoot = root || document;

    addDynamicStyles();
    initHeader(currentRoot);
    initRevealAnimations(currentRoot);
    initSmoothAnchors(currentRoot);
    initFaq(currentRoot);
    initProductSections(currentRoot);
    initParallax(currentRoot);
    initPointerGlow(currentRoot);
  }

  ready(function () {
    initialize(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:reorder', function () {
    initialize(document);
  });

  document.addEventListener('shopify:block:select', function (event) {
    const block = event.target;

    if (!block) {
      return;
    }

    block.classList.add(CLASS_NAMES.visible);

    const faqItem = block.matches(SELECTORS.faqItem)
      ? block
      : block.querySelector(SELECTORS.faqItem);

    if (faqItem) {
      faqItem.classList.add(CLASS_NAMES.open);

      const faqButton = faqItem.querySelector(SELECTORS.faqButton);

      if (faqButton) {
        faqButton.setAttribute('aria-expanded', 'true');
      }
    }
  });
})();